import memoize from 'memoize-one';
import { createSelector } from 'reselect';
import { get } from 'lodash';
import { PaymentMethods } from '../constants/paymentMethods';
import { getOrderFees } from './api';
import { getCreditCardPercentFee } from './settings';

interface RootState {
  voucherOrder: {
    isSubmittingForm: boolean;
  };
  usernameAvailability: {
    isLoading: boolean;
  };
  apiVouchers: {
    vouchers: Voucher[];
  };
}

interface Voucher {
  id: string;
  value: number;
  code: string;
  type: string;
  available: boolean;
}

interface OrderFee {
  minValue: number;
  maxValue: number;
  type: string;
  fee: number;
}

interface OrderFees {
  voucherOrderFees: OrderFee[];
  vatFeePercent: number;
}

interface OrderData {
  faceValue: number;
  quantity: number;
  voucherId: string;
}

interface OrderSummary {
  quantity: number;
  faceValue: number;
  sumVouchers: number;
  handlingFee: number;
  creditCardFee: number;
  creditCardFeePercent: number;
  vatFeePercent: number;
  vatFeeAmount: number;
  orderTotalWithoutVat: number;
  totalAmountWithFees: number;
}

export const isSubmittingForm = (state: RootState): boolean =>
  state.voucherOrder.isSubmittingForm;

export const isUsernameAvailabilityLoading = (state: RootState): boolean =>
  state.usernameAvailability.isLoading;

export const getVoucher =
  (voucherId: string) =>
  (state): Voucher | undefined =>
    (get(state, 'apiVouchers.vouchers') || []).find(v => v.id === voucherId);

export const getHandlingFee = (sumVouchers: number, type: string) =>
  createSelector(
    (state: RootState) => (getOrderFees(state) || {}).voucherOrderFees || [],
    (voucherOrderFees: OrderFee[]): number | null => {
      const handlingFeesItem = voucherOrderFees.find(
        fee =>
          fee.minValue <= sumVouchers &&
          sumVouchers <= fee.maxValue &&
          fee.type === type
      );
      if (handlingFeesItem) {
        return handlingFeesItem.fee || null;
      }
      const maxFee =
        voucherOrderFees.length > 0 &&
        voucherOrderFees
          .filter(fee => fee.type === type)
          .reduce((p, v) => (p.maxValue > v.maxValue ? p : v));

      return (maxFee || {}).fee || null;
    }
  );

export const getOrderData = (quantity: number, voucherId: string) =>
  createSelector(
    (state: RootState) => state,
    (state: RootState): OrderData => {
      const faceValue = (getVoucher(voucherId)(state) || {}).value || 0;
      return { faceValue, quantity: quantity || 0, voucherId };
    }
  );

export const commonOrderSummary = (
  paymentMethod: string,
  voucherQuantity?: number,
  voucherId?: number
) =>
  createSelector(
    () => paymentMethod === PaymentMethods.CreditCard,
    getCreditCardPercentFee,
    getOrderFees,
    (state: RootState) => state,
    (
      isCreditCardPayment: boolean,
      creditCardFeePercent: number,
      orderFees: OrderFees,
      state: RootState
    ): OrderSummary => {
      const sumVouchers = (voucherQuantity || 0) * (voucherId || 0);
      const handlingFee = getHandlingFee(sumVouchers, 'with_reference')(state);
      const creditCardFee = isCreditCardPayment
        ? ((sumVouchers + (handlingFee || 0)) * creditCardFeePercent) / 100
        : 0;

      const { vatFeePercent } = orderFees || {};
      const vatFeeAmount = ((handlingFee || 0) * vatFeePercent) / 100 || 0;
      const orderTotalWithoutVat =
        sumVouchers + (handlingFee || 0) + creditCardFee;
      const totalAmountWithFees = orderTotalWithoutVat + vatFeeAmount;
      return {
        quantity: voucherQuantity || 0,
        faceValue: voucherId || 0,
        sumVouchers,
        handlingFee: handlingFee || 0,
        creditCardFee,
        creditCardFeePercent: creditCardFee,
        vatFeePercent,
        vatFeeAmount,
        orderTotalWithoutVat,
        totalAmountWithFees
      };
    }
  );

export const orderSummary = memoize(
  (paymentMethod: string, voucherQuantity?: number, voucherId?: number) =>
    commonOrderSummary(paymentMethod, voucherQuantity, voucherId)
);
